/**
 * Social Sharing Component for Bible Buds
 * Allows sharing achievements, lessons, and milestones
 */
import { useState, useRef } from "react";
import { Share2, Copy, Check, Facebook, Twitter, MessageCircle, Mail, X, Download, Star, BookOpen, Palette, CheckCircle } from "lucide-react";

// Share types configuration
const SHARE_CONFIGS = {
  badge: {
    title: (data) => `${data.childName || 'A child'} earned a badge on Bible Buds!`,
    text: (data) => `${data.childName || 'My child'} just earned the "${data.badgeName || 'achievement'}" badge for completing Bible lessons! Join us on Bible Buds for daily faith-building activities for kids. 👉 https://biblebuds.space`,
    hashtags: ["BibleBuds", "FaithForKids", "BibleLessons"],
  },
  lesson: {
    title: (data) => `${data.childName || 'A child'} completed today's Bible lesson!`,
    text: (data) => `${data.childName || 'My child'} just finished learning about "${data.lessonTitle || "today's lesson"}" from ${data.verseReference || "the Bible"}. Bible Buds makes learning about God fun for kids! 👉 https://biblebuds.space`,
    hashtags: ["BibleBuds", "DailyBibleLesson", "KidsFaith"],
  },
  lessonComplete: {
    title: (data) => `🎉 ${data.childName || 'A child'} completed today's Bible lesson!`,
    text: (data) => {
      const lessonTitle = data.lessonTitle || "today's lesson";
      const verseRef = data.verseReference || "Today's Bible verse";
      let text = `✨ ${data.childName || 'My child'} just completed "${lessonTitle}"!\n\n`;
      text += `📖 Today's Verse: ${verseRef}\n`;
      if (data.moral) text += `💡 Lesson: "${data.moral}"\n`;
      if (data.quizScore) text += `🧠 Quiz: ${data.quizScore}\n`;
      if (data.hasDrawing) text += `🎨 Created artwork!\n`;
      text += `\n🌟 Join Bible Buds for daily faith-building lessons for kids!\n`;
      text += `👉 https://biblebuds.space`;
      return text;
    },
    hashtags: ["BibleBuds", "KidsFaith", "BibleLessons", "FamilyFaith"],
  },
  streak: {
    title: (data) => `${data.days || ''} Day Streak on Bible Buds!`,
    text: (data) => `${data.childName || 'My child'} has been learning about the Bible for ${data.days || 'many'} days in a row! Join Bible Buds for daily faith-building lessons for kids. 👉 https://biblebuds.space`,
    hashtags: ["BibleBuds", "FaithStreak", "ConsistentFaith"],
  },
  verse: {
    title: (data) => `Today's Verse: ${data.reference || ''}`,
    text: (data) => `"${data.verse || ''}" - ${data.reference || ''}\n\nShared from Bible Buds - Daily Bible lessons for kids! 👉 https://biblebuds.space`,
    hashtags: ["BibleVerse", "DailyVerse", "BibleBuds"],
  },
  familyStudy: {
    title: () => "Family Bible Study Complete!",
    text: (data) => `Our family just completed today's Bible study together on Bible Buds! "${data.title || "Today's Study"}" - Growing in faith as a family. 👉 https://biblebuds.space`,
    hashtags: ["FamilyBibleStudy", "BibleBuds", "FaithFamily"],
  },
};

// Generate share URL for different platforms
const getShareUrl = (platform, text, url, hashtags = []) => {
  const encodedText = encodeURIComponent(text);
  const encodedUrl = encodeURIComponent(url);
  const hashtagString = hashtags.join(",");

  switch (platform) {
    case "facebook":
      // Facebook prefers just the URL, it will fetch OG tags
      return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`;
    case "facebook-mobile":
      // Mobile Facebook app deep link
      return `fb://facewebmodal/f?href=${encodedUrl}`;
    case "twitter":
      return `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}&hashtags=${hashtagString}`;
    case "whatsapp":
      return `https://wa.me/?text=${encodedText}%20${encodedUrl}`;
    case "email":
      return `mailto:?subject=${encodeURIComponent("Check out Bible Buds!")}&body=${encodedText}%0A%0A${encodedUrl}`;
    default:
      return url;
  }
};

// Check if device is mobile
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Main Share Button Component
export function ShareButton({ 
  type, 
  data, 
  className = "",
  buttonText = "Share",
  showIcon = true,
  size = "default" // "small", "default", "large"
}) {
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const config = SHARE_CONFIGS[type];
  if (!config) return null;

  const shareUrl = data.url || "https://biblebuds.space";
  const shareTitle = config.title(data);
  const shareText = config.text(data);
  const hashtags = config.hashtags;

  // Convert base64 drawing to blob for sharing
  const dataURLtoBlob = (dataURL) => {
    if (!dataURL) return null;
    try {
      const arr = dataURL.split(',');
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while(n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new Blob([u8arr], {type: mime});
    } catch (e) {
      console.error("Failed to convert image:", e);
      return null;
    }
  };

  const handleNativeShare = async () => {
    // Check if native sharing is available
    if (navigator.share) {
      try {
        const shareData = {
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        };

        // Try to include drawing as file if available and supported
        if (data.drawingData && navigator.canShare) {
          const blob = dataURLtoBlob(data.drawingData);
          if (blob) {
            const file = new File([blob], `${data.childName || 'child'}-bible-buds-artwork.png`, { type: 'image/png' });
            const filesShareData = { ...shareData, files: [file] };
            
            if (navigator.canShare(filesShareData)) {
              await navigator.share(filesShareData);
              return;
            }
          }
        }

        // Fallback to text-only share
        await navigator.share(shareData);
      } catch (err) {
        if (err.name !== "AbortError") {
          setShowModal(true);
        }
      }
    } else {
      setShowModal(true);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText}\n\n${shareUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const sizeClasses = {
    small: "px-3 py-1.5 text-xs gap-1",
    default: "px-4 py-2 text-sm gap-2",
    large: "px-6 py-3 text-base gap-2",
  };

  return (
    <>
      <button
        onClick={handleNativeShare}
        className={`flex items-center justify-center font-bold rounded-xl transition-all hover:scale-105 active:scale-95 ${sizeClasses[size]} ${className}`}
        data-testid={`share-${type}-btn`}
      >
        {showIcon && <Share2 size={size === "small" ? 14 : size === "large" ? 20 : 16} />}
        {buttonText}
      </button>

      {/* Share Modal */}
      {showModal && (
        <ShareModal
          title={shareTitle}
          text={shareText}
          url={shareUrl}
          hashtags={hashtags}
          copied={copied}
          onCopy={handleCopyLink}
          onClose={() => setShowModal(false)}
          shareData={type === 'lessonComplete' ? { ...data, type } : null}
        />
      )}
    </>
  );
}

// Share Modal Component
function ShareModal({ title, text, url, hashtags, copied, onCopy, onClose, shareData }) {
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  
  const platforms = [
    { id: "facebook", name: "Facebook", icon: Facebook, color: "#1877F2", bg: "#E7F3FF" },
    { id: "twitter", name: "Twitter/X", icon: Twitter, color: "#1DA1F2", bg: "#E8F5FD" },
    { id: "whatsapp", name: "WhatsApp", icon: MessageCircle, color: "#25D366", bg: "#E7FFF1" },
    { id: "email", name: "Email", icon: Mail, color: "#EA4335", bg: "#FEEAE9" },
  ];

  const isLessonComplete = shareData?.type === 'lessonComplete';

  // Handle Facebook share with popup window
  const handleFacebookShare = (e) => {
    e.preventDefault();
    const fbUrl = getShareUrl("facebook", text, url, hashtags);
    window.open(fbUrl, 'facebook-share', 'width=600,height=500,menubar=no,toolbar=no,resizable=yes,scrollbars=yes');
  };

  // Handle native mobile share with drawing support
  const handleMobileNativeShare = async () => {
    if (navigator.share) {
      try {
        const sharePayload = {
          title: title,
          text: text,
          url: url,
        };

        // Try to include drawing as a file if available
        if (shareData?.drawingData && navigator.canShare) {
          try {
            // Convert base64 to blob
            const base64Response = await fetch(shareData.drawingData);
            const blob = await base64Response.blob();
            const file = new File([blob], `${shareData.childName || 'bible-buds'}-artwork.png`, { type: 'image/png' });
            
            const filesPayload = { ...sharePayload, files: [file] };
            if (navigator.canShare(filesPayload)) {
              await navigator.share(filesPayload);
              onClose();
              return;
            }
          } catch (imgErr) {
            console.log("Could not share with image, sharing text only");
          }
        }

        await navigator.share(sharePayload);
        onClose();
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Share failed:", err);
        }
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50" onClick={onClose}>
      <div 
        className="bg-white w-full sm:rounded-3xl sm:max-w-md overflow-hidden shadow-2xl max-h-[95vh] overflow-y-auto rounded-t-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between sticky top-0 z-10"
          style={{ background: "linear-gradient(135deg, #EF476F, #FFD166, #06D6A0, #118AB2)" }}>
          <div className="flex items-center gap-2">
            <Share2 size={20} color="white" />
            <h3 className="font-black text-white text-lg" style={{ fontFamily: "'Baloo 2', cursive" }}>
              {isLessonComplete ? "🎉 Share Achievement!" : "Share"}
            </h3>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white bg-white/20 rounded-full p-1.5">
            <X size={20} />
          </button>
        </div>

        {/* Mobile Native Share Button - Prominent on mobile devices */}
        {isMobile && navigator.share && (
          <div className="p-4 bg-gradient-to-r from-[#06D6A0]/10 to-[#118AB2]/10 border-b">
            <button
              onClick={handleMobileNativeShare}
              className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl bg-gradient-to-r from-[#06D6A0] to-[#118AB2] text-white font-black text-lg shadow-lg active:scale-95 transition-transform"
              style={{ fontFamily: "'Baloo 2', cursive" }}
              data-testid="mobile-native-share"
            >
              <Share2 size={24} />
              Share Now 📱
            </button>
            <p className="text-center text-xs text-gray-500 mt-2">
              Opens your device's share menu with all your apps
            </p>
          </div>
        )}

        {/* Colorful Share Card Preview for Lesson Complete */}
        {isLessonComplete && shareData ? (
          <div className="p-4">
            <LessonCompleteShareCard data={shareData} />
          </div>
        ) : (
          /* Regular Preview */
          <div className="p-4 bg-gray-50 border-b">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">Preview</p>
            <p className="text-sm text-gray-700 leading-relaxed line-clamp-4">{text}</p>
          </div>
        )}

        {/* Share Options */}
        <div className="p-4 bg-gray-50">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">
            {isMobile && navigator.share ? "Or share directly to:" : "Share via"}
          </p>
          <div className="grid grid-cols-4 gap-3">
            {platforms.map(({ id, name, icon: Icon, color, bg }) => (
              id === 'facebook' ? (
                <button
                  key={id}
                  onClick={handleFacebookShare}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-2xl transition-all hover:scale-105 active:scale-95"
                  style={{ background: bg }}
                  data-testid={`share-${id}`}
                >
                  <Icon size={24} color={color} />
                  <span className="text-xs font-bold" style={{ color }}>{name}</span>
                </button>
              ) : (
                <a
                  key={id}
                  href={getShareUrl(id, text, url, hashtags)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-1.5 p-3 rounded-2xl transition-all hover:scale-105 active:scale-95"
                  style={{ background: bg }}
                  data-testid={`share-${id}`}
                >
                  <Icon size={24} color={color} />
                  <span className="text-xs font-bold" style={{ color }}>{name}</span>
                </a>
              )
            ))}
          </div>

          {/* Copy Link */}
          <button
            onClick={onCopy}
            className="w-full mt-4 flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-gray-200 font-bold text-gray-600 hover:bg-white active:scale-95 transition-all"
            data-testid="share-copy-link"
          >
            {copied ? (
              <>
                <Check size={18} color="#06D6A0" />
                <span className="text-[#06D6A0]">Copied!</span>
              </>
            ) : (
              <>
                <Copy size={18} />
                <span>Copy Message</span>
              </>
            )}
          </button>
          
          {/* Download artwork button for lesson complete shares */}
          {isLessonComplete && shareData?.drawingData && (
            <a
              href={shareData.drawingData}
              download={`${shareData.childName || 'bible-buds'}-artwork.png`}
              className="w-full mt-3 flex items-center justify-center gap-2 py-3 rounded-xl bg-[#9B5DE5] text-white font-bold hover:bg-[#8a4ed4] active:scale-95 transition-all"
              data-testid="download-artwork"
            >
              <Download size={18} />
              <span>Save Artwork to Share</span>
            </a>
          )}
          
          {/* Bible Buds Link */}
          <div className="mt-4 text-center pb-2">
            <p className="text-xs text-gray-400 mb-1">Learn more at</p>
            <a 
              href="https://biblebuds.space" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm font-bold text-[#118AB2] hover:underline"
            >
              biblebuds.space
            </a>
          </div>
        </div>
        
        {/* Safe area padding for mobile */}
        <div className="h-4 sm:h-0 bg-gray-50"></div>
      </div>
    </div>
  );
}

// Colorful Lesson Complete Share Card Component
function LessonCompleteShareCard({ data }) {
  return (
    <div className="rounded-3xl overflow-hidden shadow-lg border-4 border-[#FFD166]"
      style={{ background: "linear-gradient(180deg, #FFF9E6 0%, #FFFFFF 100%)" }}>
      
      {/* Header with rainbow gradient */}
      <div className="p-4 text-center relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #EF476F 0%, #FFD166 25%, #06D6A0 50%, #118AB2 75%, #9B5DE5 100%)" }}>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1 left-2 text-2xl">⭐</div>
          <div className="absolute top-2 right-4 text-xl">✨</div>
          <div className="absolute bottom-1 left-1/4 text-lg">🌟</div>
          <div className="absolute bottom-2 right-1/3 text-xl">⭐</div>
        </div>
        <p className="text-white/90 text-xs font-bold uppercase tracking-wider mb-1">Bible Buds</p>
        <h3 className="text-white font-black text-xl drop-shadow-md" style={{ fontFamily: "'Baloo 2', cursive" }}>
          🎉 Lesson Complete! 🎉
        </h3>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3">
        {/* Child name and lesson */}
        <div className="text-center">
          <p className="font-black text-2xl text-[#073B4C]" style={{ fontFamily: "'Baloo 2', cursive" }}>
            {data.childName}
          </p>
          <p className="text-gray-500 text-sm font-semibold">just finished learning!</p>
        </div>
        
        {/* Lesson Title */}
        <div className="bg-[#E0F4FF] rounded-2xl p-3 border-l-4 border-[#118AB2]">
          <div className="flex items-center gap-2 mb-1">
            <BookOpen size={16} color="#118AB2" />
            <span className="text-xs font-bold text-[#118AB2] uppercase">Today's Lesson</span>
          </div>
          <p className="font-bold text-[#073B4C]" style={{ fontFamily: "'Quicksand', sans-serif" }}>
            {data.lessonTitle}
          </p>
        </div>

        {/* Bible Verse */}
        {data.verseReference && (
          <div className="bg-[#F0E6FF] rounded-2xl p-3 border-l-4 border-[#9B5DE5]">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm">📖</span>
              <span className="text-xs font-bold text-[#9B5DE5] uppercase">Bible Verse</span>
            </div>
            <p className="text-[#073B4C] text-sm font-semibold">{data.verseReference}</p>
          </div>
        )}

        {/* Moral/Lesson Learned */}
        {data.moral && (
          <div className="bg-[#FFF0F3] rounded-2xl p-3 border-l-4 border-[#EF476F]">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm">💡</span>
              <span className="text-xs font-bold text-[#EF476F] uppercase">What I Learned</span>
            </div>
            <p className="text-[#073B4C] text-sm italic">"{data.moral}"</p>
          </div>
        )}

        {/* Drawing Preview */}
        {data.drawingData && (
          <div className="bg-[#E8FFF5] rounded-2xl p-3 border-l-4 border-[#06D6A0]">
            <div className="flex items-center gap-2 mb-2">
              <Palette size={16} color="#06D6A0" />
              <span className="text-xs font-bold text-[#06D6A0] uppercase">My Artwork</span>
            </div>
            <div className="rounded-xl overflow-hidden border-2 border-[#06D6A0]/30">
              <img 
                src={data.drawingData} 
                alt="Lesson artwork" 
                className="w-full h-32 object-contain bg-white"
              />
            </div>
          </div>
        )}

        {/* Quiz Score */}
        {data.quizScore && (
          <div className="bg-[#FFF3CD] rounded-2xl p-3 border-l-4 border-[#F77F00]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm">🧠</span>
                <span className="text-xs font-bold text-[#F77F00] uppercase">Quiz Result</span>
              </div>
              <div className="flex items-center gap-1">
                {[...Array(parseInt(data.quizScore.split('/')[0]) || 0)].map((_, i) => (
                  <Star key={i} size={16} fill="#F77F00" color="#F77F00" />
                ))}
                <span className="font-black text-[#F77F00] ml-1">{data.quizScore}</span>
              </div>
            </div>
          </div>
        )}

        {/* Reflection Answer */}
        {data.reflectionAnswer && (
          <div className="bg-white rounded-2xl p-3 border-2 border-gray-200">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm">✏️</span>
              <span className="text-xs font-bold text-gray-500 uppercase">My Thoughts</span>
            </div>
            <p className="text-[#073B4C] text-sm italic">"{data.reflectionAnswer}"</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-3 text-center border-t bg-gradient-to-r from-[#118AB2]/10 via-[#06D6A0]/10 to-[#FFD166]/10">
        <p className="text-xs text-gray-500 font-medium">
          Join us for daily Bible lessons! 👉 <span className="font-bold text-[#118AB2]">biblebuds.space</span>
        </p>
      </div>
    </div>
  );
}

// Quick Share Icon Button (for inline use)
export function QuickShareButton({ type, data, className = "" }) {
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);

  const config = SHARE_CONFIGS[type];
  if (!config) return null;

  const shareUrl = data.url || "https://biblebuds.space";
  const shareTitle = config.title(data);
  const shareText = config.text(data);
  const hashtags = config.hashtags;

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        if (err.name !== "AbortError") {
          setShowModal(true);
        }
      }
    } else {
      setShowModal(true);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText}\n\n${shareUrl}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  return (
    <>
      <button
        onClick={handleShare}
        className={`p-2 rounded-full hover:bg-gray-100 transition-all ${className}`}
        data-testid={`quick-share-${type}`}
      >
        <Share2 size={18} className="text-gray-500" />
      </button>

      {showModal && (
        <ShareModal
          title={shareTitle}
          text={shareText}
          url={shareUrl}
          hashtags={hashtags}
          copied={copied}
          onCopy={handleCopyLink}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}

export default ShareButton;
